import { useState, useEffect, useRef } from 'react';
import SideMenu from '../components/Menu';
import { useNavigate } from 'react-router-dom';
import PaperDetailsModal from '../components/modals/PaperDetailsModal';
import LoadingModal from '../components/modals/LoadingModal';
import { Study } from '../types/Study';

// Define your Paper interface (a subset of Study)
interface Paper {
  id: string;
  title: string;
  authors: string;
  description: string;
  source: string;
  publicationYear?: number;
  doi?: string;
  citationCount?: number;
  openAccess?: boolean;
  publicationType?: string;
}

const PublishedPapers = () => {
  const [activeTab, setActiveTab] = useState<'authored' | 'bookmarked'>('authored');
  const [papers, setPapers] = useState<Paper[]>([]);
  // We'll store a full Study in state for the modal.
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageRange, setPageRange] = useState([1, 9]);
  const [totalPages, setTotalPages] = useState(1);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [publicationTypeFilter, setPublicationTypeFilter] = useState<string>('');
  const [noOpenAlexId, setNoOpenAlexId] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const activeTabRef = useRef<'authored' | 'bookmarked'>(activeTab);

  const navigate = useNavigate();

  const fetchPapers = async (
    tab: 'authored' | 'bookmarked',
    pageNumber = 1,
    search = '',
    typeFilter = ''
  ) => {
    activeTabRef.current = tab;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);
    setNoOpenAlexId(false);

    try {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        per_page: '25',
      });

      if (search) {
        queryParams.append('search', search);
      }

      if (typeFilter && tab === 'authored') {
        queryParams.append('type', typeFilter);
      }

      const response = await fetch(
        `https://academichub-backend.onrender.com/papers/${tab}?${queryParams.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          signal: abortController.signal,
        }
      );

      if (activeTabRef.current !== tab) return;

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.error && errorData.error.includes('No OpenAlex ID')) {
          setNoOpenAlexId(true);
        }

        throw new Error(errorData.error || 'Failed to fetch papers');
      }

      const data = await response.json();

      if (activeTabRef.current === tab) {
        setPapers(data.papers || []);
        setTotalPages(data.totalPages || Math.ceil((data.count || 0) / 25));
        setCurrentPage(pageNumber);
        setInitialLoadCompleted(true);

        if (pageNumber === 1) {
          setPageRange([1, Math.min(9, data.totalPages || 1)]);
        } else if (pageNumber < pageRange[0]) {
          setPageRange([Math.max(1, pageNumber - 4), pageNumber + 4]);
        } else if (pageNumber > pageRange[1]) {
          setPageRange([pageNumber - 4, Math.min(pageNumber + 4, data.totalPages || 1)]);
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      if (activeTabRef.current === tab) {
        console.error('Error fetching papers:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load papers. Please try again later.'
        );
        setPapers([]);
        setInitialLoadCompleted(true);
      }
    } finally {
      if (activeTabRef.current === tab) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setInitialLoadCompleted(false);
    if (!term.trim()) {
      fetchPapers(activeTab, 1, '', publicationTypeFilter);
    } else {
      setCurrentPage(1);
      fetchPapers(activeTab, 1, term, publicationTypeFilter);
    }
  };

  const handleTypeFilterChange = (type: string) => {
    if (activeTab === 'authored') {
      setPublicationTypeFilter(type);
      setCurrentPage(1);
      setInitialLoadCompleted(false);
      fetchPapers(activeTab, 1, searchTerm, type);
    }
  };

  useEffect(() => {
    activeTabRef.current = activeTab;
    setPapers([]);
    setPublicationTypeFilter('');
    setInitialLoadCompleted(false);

    if (searchTerm.trim()) {
      fetchPapers(activeTab, 1, searchTerm, '');
    } else {
      fetchPapers(activeTab, 1, '', '');
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [activeTab]);

  const handleTabChange = (tab: 'authored' | 'bookmarked') => {
    setActiveTab(tab);
    setSearchTerm('');
    setPublicationTypeFilter('');
    setInitialLoadCompleted(false);
    fetchPapers(tab, 1, '', '');
  };

  const handlePageChange = (pageNumber: number) => {
    setInitialLoadCompleted(false);
    const typeFilter = activeTab === 'authored' ? publicationTypeFilter : '';
    fetchPapers(activeTab, pageNumber, searchTerm, typeFilter);
  };

  const handlePageRange = (next: boolean) => () => {
    if (next) {
      if (pageRange[1] < totalPages) {
        const newRange = [pageRange[0] + 9, Math.min(pageRange[1] + 9, totalPages)];
        setPageRange(newRange);
        if (currentPage < newRange[0]) {
          handlePageChange(newRange[0]);
        }
      }
    } else {
      if (pageRange[0] > 1) {
        const newRange = [Math.max(1, pageRange[0] - 9), pageRange[0] - 1];
        setPageRange(newRange);
        if (currentPage > newRange[1]) {
          handlePageChange(newRange[1]);
        }
      }
    }
  };

  const handleFindNewPapers = () => {
    navigate('/');
  };

  const handleRemoveBookmark = async (paperId: string) => {
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/papers/bookmark/${paperId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove bookmark');
      }

      fetchPapers(activeTab, currentPage);

      setNotification({
        message: 'Bookmark removed successfully!',
        type: 'success',
      });

      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (err) {
      console.error('Error removing bookmark:', err);
      setNotification({
        message: err instanceof Error ? err.message : 'Failed to remove bookmark',
        type: 'error',
      });

      setTimeout(() => {
        setNotification(null);
      }, 3000);
    }
  };

  const shouldShowPagination = totalPages > 1;

  const handlePaperAnalytics = async (paperId: string | undefined) => {
    if (!paperId) return;
    setModalLoading(true);

    try {
      const url = `https://academichub-backend.onrender.com/paperanalytics?id=${paperId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch paper analytics');
      }

      const data = await response.json();

      setSelectedStudy(data);
    } catch (error) {
      console.error('Error fetching paper analytics:', error);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <SideMenu />
      <div
        id="published-papers"
        className="p-3 h-screen bg-accent w-full"
        style={{ backgroundColor: '#5BC0BE' }}
      >
        {notification && (
          <div
            className={`fixed top-4 right-4 z-50 p-3 rounded-md shadow-md ${
              notification.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {notification.message}
          </div>
        )}

        <div className="bg-white rounded-lg h-full flex flex-col overflow-hidden">
          <div className="pt-6 px-4 sm:px-8 pb-2 flex-shrink-0">
            <h1 className="text-3xl sm:text-4xl font-bold text-center" style={{ color: '#0B132B' }}>
              My papers
            </h1>
            <div className="flex flex-col sm:flex-row mt-6 border-b">
              <div className="flex">
                <button
                  className={`px-4 py-2 mr-2 rounded-t-lg transition-colors duration-200 ${
                    activeTab === 'authored' ? 'text-white' : 'text-black'
                  }`}
                  style={{
                    backgroundColor: activeTab === 'authored' ? '#1C2541' : '#e2e8f0',
                  }}
                  onClick={() => handleTabChange('authored')}
                >
                  Authored
                </button>
                <button
                  className={`px-4 py-2 rounded-t-lg transition-colors duration-200 ${
                    activeTab === 'bookmarked' ? 'text-white' : 'text-black'
                  }`}
                  style={{
                    backgroundColor: activeTab === 'bookmarked' ? '#1C2541' : '#e2e8f0',
                  }}
                  onClick={() => handleTabChange('bookmarked')}
                >
                  Bookmarked
                </button>
              </div>
              <div className="sm:ml-auto flex items-center mt-2 sm:mt-0 mb-2 sm:mb-0">
                <div className="relative w-full sm:w-auto flex items-center">
                  <div className="flex"></div>
                  <input
                    type="text"
                    placeholder="Search"
                    className="border rounded-md px-2 py-1 text-sm w-full min-w-[160px]"
                    value={searchTerm}
                    onChange={e => handleSearch(e.target.value)}
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {activeTab === 'authored' && (
              <div className="flex flex-wrap mt-2 mb-1">
                <div className="mr-2 text-sm font-medium mt-1">Filter by type:</div>
                <button
                  onClick={() => handleTypeFilterChange('')}
                  className={`px-2 py-1 mr-2 text-xs rounded-md mb-1 ${
                    publicationTypeFilter === ''
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleTypeFilterChange('Journal')}
                  className={`px-2 py-1 mr-2 text-xs rounded-md mb-1 ${
                    publicationTypeFilter === 'Journal'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Journal
                </button>
                <button
                  onClick={() => handleTypeFilterChange('Conference')}
                  className={`px-2 py-1 mr-2 text-xs rounded-md mb-1 ${
                    publicationTypeFilter === 'Conference'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Conference
                </button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4">
            {loading || !initialLoadCompleted ? (
              <div className="flex justify-center items-center h-full">
                <div
                  className="loader animate-spin rounded-full h-12 w-12 border-t-4 border-b-4"
                  style={{ borderColor: '#5BC0BE' }}
                ></div>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">{error}</div>
            ) : papers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm.trim() || publicationTypeFilter
                  ? 'No papers matching your filters'
                  : activeTab === 'authored'
                  ? noOpenAlexId
                    ? 'No OpenAlex ID associated with this account. Please set your OpenAlex ID in your profile.'
                    : 'No authored papers found.'
                  : "You haven't bookmarked any papers yet."}
              </div>
            ) : (
              <div className="space-y-4">
                {papers.map(paper => (
                  <div key={paper.id} className="border-b pb-4">
                    <div className="flex justify-between items-start">
                      <h3
                        className="text-lg font-medium cursor-pointer"
                        style={{ color: '#0B132B' }}
                        onClick={() => handlePaperAnalytics(paper.id)}
                      >
                        {paper.title}
                      </h3>
                      <a
                        href={
                          paper.doi ? `https://doi.org/${paper.doi}` : `https://${paper.source}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm hover:underline"
                        style={{ color: '#3A506B' }}
                      >
                        {paper.source}
                      </a>
                    </div>
                    <p className="text-sm" style={{ color: '#5BC0BE' }}>
                      {paper.authors}
                    </p>
                    <p className="text-sm mt-1 text-gray-700">{paper.description}</p>
                    <div className="flex mt-2 text-xs text-gray-500 justify-between items-center">
                      <div>
                        {paper.publicationYear && (
                          <span className="mr-4">Year: {paper.publicationYear}</span>
                        )}
                        {paper.citationCount !== undefined && (
                          <span className="mr-4">Citations: {paper.citationCount}</span>
                        )}
                        {paper.publicationType && (
                          <span className="mr-4 px-2 py-0.5 bg-gray-100 rounded-md">
                            {paper.publicationType}
                          </span>
                        )}
                      </div>
                      {activeTab === 'bookmarked' && (
                        <button
                          onClick={() => handleRemoveBookmark(paper.id)}
                          className="text-sm text-red-500 hover:text-red-700"
                          title="Remove bookmark"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!loading && shouldShowPagination && (
            <div className="flex justify-center my-4 flex-shrink-0">
              <div className="flex items-center">
                {currentPage > 1 && (
                  <button
                    className="mx-1 px-3 py-1 rounded hover:bg-gray-200"
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    &larr;
                  </button>
                )}

                {pageRange[0] > 1 && (
                  <button
                    className="mx-1 px-3 py-1 rounded hover:bg-gray-200"
                    onClick={handlePageRange(false)}
                  >
                    ...
                  </button>
                )}

                {Array.from(
                  { length: pageRange[1] - pageRange[0] + 1 },
                  (_, i) => pageRange[0] + i
                ).map(pageNumber => (
                  <button
                    key={pageNumber}
                    className={`mx-1 px-3 py-1 rounded ${
                      currentPage === pageNumber ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'
                    }`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}

                {pageRange[1] < totalPages && (
                  <button
                    className="mx-1 px-3 py-1 rounded hover:bg-gray-200"
                    onClick={handlePageRange(true)}
                  >
                    ...
                  </button>
                )}

                {currentPage < totalPages && (
                  <button
                    className="mx-1 px-3 py-1 rounded hover:bg-gray-200"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    &rarr;
                  </button>
                )}
              </div>
            </div>
          )}
          {(searchTerm || publicationTypeFilter) && (
            <div className="text-center mb-4 text-sm text-gray-600">
              <p>
                {searchTerm && (
                  <>
                    Showing search results for &quot;{searchTerm}&quot;
                    {publicationTypeFilter && ` in ${publicationTypeFilter} publications`}.
                  </>
                )}
                {!searchTerm && publicationTypeFilter && (
                  <>Showing only {publicationTypeFilter} publications.</>
                )}
                <button
                  className="text-blue-500 hover:underline ml-2"
                  onClick={() => {
                    setSearchTerm('');
                    setPublicationTypeFilter('');
                    fetchPapers(activeTab, currentPage);
                  }}
                >
                  Clear filters
                </button>
              </p>
            </div>
          )}
          <div className="p-4 flex justify-center flex-shrink-0">
            <button
              onClick={handleFindNewPapers}
              className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-md transition-opacity hover:opacity-90"
            >
              Find new papers
            </button>
          </div>
        </div>
      </div>
      {/* Render the PaperDetailsModal when a Study is selected */}
      {selectedStudy && (
        <PaperDetailsModal
          isOpen={!!selectedStudy}
          onClose={() => setSelectedStudy(null)}
          study={selectedStudy}
        />
      )}
      {modalLoading && <LoadingModal onClose={() => setSelectedStudy(null)} />}
    </>
  );
};

export default PublishedPapers;
